import { TaskStatus, WorkflowStepCode } from '@exam-management/graphql';
import { sortByTaskCode } from '@exam-management/task-management';
import groupBy from 'lodash/groupBy';

import type { QSetReviewTaskContextData } from '../providers';
import type { QSetReviewTaskDetailsFormInput } from '../schemas';
import type { SubtaskDetails } from '../types';

import isAddedSubtaskKey from './is-add-subtask-key';

function mapAssignee(subtask: QSetReviewTaskDetailsFormInput['subtask'][string]) {
  if (!subtask?.assignee) {
    return { id: '', firstname: '', lastname: '' };
  }

  const [firstname = '', lastname = ''] = subtask.assignee.inputValue?.split(' ') ?? [];
  return {
    id: subtask.assignee.value ?? '',
    firstname,
    lastname,
  };
}

function mapWorkflowStep(subtaskBaseData: SubtaskDetails, isAddedSubtask: boolean) {
  if (isAddedSubtask) {
    return {
      name: '',
      code: WorkflowStepCode.ReviewQsetTodo,
    };
  }

  return {
    name: subtaskBaseData?.currentWorkflowStep?.name ?? '',
    code: subtaskBaseData?.currentWorkflowStep?.code ?? WorkflowStepCode.ReviewQsetTodo,
  };
}

function mapSubtask(
  subtaskCode: string,
  subtask: QSetReviewTaskDetailsFormInput['subtask'][string],
  subtaskBaseData: SubtaskDetails,
  isAddedSubtask: boolean,
) {
  return {
    id: isAddedSubtask ? '' : (subtaskBaseData?.id ?? ''),
    code: isAddedSubtask ? subtaskCode : (subtaskBaseData?.code ?? ''),
    qsetId: subtaskBaseData?.qsetId ?? '',
    qsetCode: subtaskBaseData?.qsetCode ?? '',
    dueDate: subtask?.dueDate ?? new Date(),
    qConfigCode: subtaskBaseData?.qConfigCode ?? '',
    assignee: mapAssignee(subtask),
    currentWorkflowStep: mapWorkflowStep(subtaskBaseData, isAddedSubtask),
    status: isAddedSubtask ? TaskStatus.Todo : (subtaskBaseData?.status ?? TaskStatus.Todo),
    createdAt: isAddedSubtask ? new Date() : (subtaskBaseData?.createdAt ?? new Date()),
    totalQuestions: subtaskBaseData?.totalQuestions ?? 0,
    timeLimits: subtaskBaseData?.timeLimits ?? [],
    submittedAt: isAddedSubtask ? null : (subtaskBaseData?.submittedAt ?? null),
  };
}

export default function mapSubtaskFormDataToSubtaskDetails(
  formData: QSetReviewTaskDetailsFormInput['subtask'],
  subtaskDetails: QSetReviewTaskContextData['mainTaskDetail']['subtasks'],
): Record<string, SubtaskDetails[]> {
  const mappedSubtaskDetails = Object.entries(formData).map(([subtaskCode, subtask]) => {
    const isAddedSubtask = isAddedSubtaskKey(subtaskCode);
    const subtaskBaseData = subtaskDetails.find(
      (task) => subtaskCode === task.code || (isAddedSubtask && task.qsetId === subtask.qSetId),
    ) as SubtaskDetails;

    return mapSubtask(subtaskCode, subtask, subtaskBaseData, isAddedSubtask);
  });

  mappedSubtaskDetails.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  const groupedTasks = groupBy(mappedSubtaskDetails, (task) => task.qsetCode);

  return sortByTaskCode(groupedTasks) as Record<string, SubtaskDetails[]>;
}
